import React, { useMemo, useState } from 'react';
import { Select, Skeleton } from 'antd';
import type { Rule } from 'antd/es/form';
import { useGetStoresPaginated } from '@/store/Dashboard/Stores/useGetStoresPaginated';
import { handleInfiniteScroll } from '@/components/ModalAddPlanogramFromProduct/_components/FormPlanogram/utils';
import { useDebounce } from '@/hooks/useDebounce';
import {
    formatProductCount,
    getSearchTerm,
} from '@/pages/PlanogramQuickAdjust/utils';
import * as S from './styles';

const { Option } = Select;

type StoreBarProps = {
    storeId: number | undefined;
    onStoreChange: (storeId: number) => void;
    totalProducts: number;
    isLoading?: boolean;
    disabled?: boolean;
    /** Shown while the selected store is not in the loaded options page. */
    storeName?: string;
    /**
     * Wraps the select in a `Form.Item`, so the error shows under the field.
     * Requires a parent `Form`.
     */
    validation?: { name: string; rules: Rule[] };
    className?: string;
};

export const StoreBar = ({
    storeId,
    onStoreChange,
    totalProducts,
    isLoading,
    disabled = false,
    storeName,
    validation,
    className,
}: StoreBarProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 400);

    const {
        data: storesPaginated,
        hasNextPage,
        fetchNextPage,
        isFetching,
    } = useGetStoresPaginated(getSearchTerm(debouncedSearchTerm));

    const storeOptions = useMemo(
        () => storesPaginated?.pages.flatMap((page) => page.data) ?? [],
        [storesPaginated],
    );

    const storeSelect = (
        <S.StoreSelect
            showSearch
            disabled={disabled}
            placeholder="Selecione a loja"
            value={storeId}
            onChange={(value) => onStoreChange(value as number)}
            labelRender={
                storeName
                    ? ({ label, value }) => label ?? storeName ?? value
                    : undefined
            }
            filterOption={false}
            onSearch={setSearchTerm}
            onPopupScroll={(e) =>
                handleInfiniteScroll({
                    e,
                    hasNextPage,
                    isFetching,
                    fetchNextPage,
                })
            }
        >
            {storeOptions.map((store) => (
                <Option key={store.id} value={store.id}>
                    {store.name}
                </Option>
            ))}
        </S.StoreSelect>
    );

    return (
        <S.Bar className={className}>
            <S.IconBox>
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M2 7 4 3h16l2 4" />
                    <path d="M4 7v13a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V7" />
                    <path d="M2 7h20" />
                    <path d="M9 21v-6h6v6" />
                </svg>
            </S.IconBox>
            <S.StoreColumn>
                <S.StoreLabel>Operando a loja</S.StoreLabel>
                {validation ? (
                    <S.StoreField
                        name={validation.name}
                        rules={validation.rules}
                    >
                        {storeSelect}
                    </S.StoreField>
                ) : (
                    storeSelect
                )}
            </S.StoreColumn>
            {!!storeId && (
                <S.Summary>
                    {isLoading ? (
                        <Skeleton.Input
                            active
                            size="small"
                            style={{ width: 80, minWidth: 0, height: 16 }}
                        />
                    ) : (
                        formatProductCount(totalProducts)
                    )}
                </S.Summary>
            )}
        </S.Bar>
    );
};

export default StoreBar;
